import {
  captionWithUrl,
  shareIntentUrl,
  trackedLandingUrl,
  youtubeWatchUrl,
  type IntentPlatform,
  type Locale,
  type PublicClip,
  type SharePlatform,
} from "@kmstories/shared";
import { useEffect, useId, useState } from "react";
import { useT } from "../i18n/LocaleProvider.js";
import { beacon } from "../lib/api.js";
import { useToast } from "./Toast.js";

/**
 * Share-intents in the order this audience actually uses: Telegram and
 * WhatsApp first. Instagram and TikTok have no web intent, so their buttons
 * copy the caption. X gets the raw YouTube URL (it plays inline there). The
 * caption is an editable starting point in either language.
 */
export function ShareBar({ clip, captions }: { clip: PublicClip; captions: Record<Locale, string> }) {
  const { m, locale, rtl } = useT();
  const toast = useToast();
  const id = useId();
  const [capLocale, setCapLocale] = useState<Locale>(locale);
  const [caption, setCaption] = useState(captions[locale]);
  const [canNative, setCanNative] = useState(false);

  useEffect(() => {
    setCaption(captions[capLocale]);
  }, [capLocale, captions]);
  useEffect(() => {
    setCanNative(typeof navigator !== "undefined" && typeof navigator.share === "function");
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const landingBase = `${origin}${locale === "fa" ? "/fa" : ""}/c/${clip.slug}`;
  const landing = (platform: SharePlatform) => trackedLandingUrl(landingBase, platform, clip.slug);
  const youtube = youtubeWatchUrl(clip.youtubeId);
  const captionRtl = capLocale === "fa";

  const record = (platform: SharePlatform) => beacon("/api/share-events", { clipSlug: clip.slug, platform, locale });

  const intents: IntentPlatform[] = ["telegram", "whatsapp", "x", "facebook", "bluesky", "linkedin", "reddit"];

  async function copyText(text: string, done: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast(done);
    } catch {
      // Clipboard blocked: show the text so it can be selected by hand.
      window.prompt(done, text);
    }
  }

  async function nativeShare() {
    record("webshare");
    try {
      await navigator.share({ title: clip.title, text: caption, url: landing("webshare") });
    } catch {
      // user dismissed the sheet
    }
  }

  return (
    <section className="sharebar" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} style={{ fontSize: "1.25rem", margin: 0 }}>
        {m.shareBar.heading}
      </h2>

      <div className="field">
        <div className="recorder__row" style={{ justifyContent: "space-between" }}>
          <label htmlFor={`${id}-cap`} className="field__label">
            {m.shareBar.captionLabel}
          </label>
          <div className="sharebar__tabs" role="tablist" aria-label={m.nav.language}>
            {(["en", "fa"] as Locale[]).map((l) => (
              <button
                key={l}
                type="button"
                role="tab"
                className="sharebar__tab"
                aria-selected={capLocale === l}
                lang={l}
                onClick={() => setCapLocale(l)}
              >
                {l === "en" ? "English" : "فارسی"}
              </button>
            ))}
          </div>
        </div>
        <textarea
          id={`${id}-cap`}
          className="textarea"
          style={{ minHeight: "5.5rem" }}
          dir={captionRtl ? "rtl" : "ltr"}
          lang={capLocale}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        <p className="field__hint">{m.shareBar.captionHint}</p>
      </div>

      <div className="sharebar__buttons">
        {canNative && (
          <button type="button" className="sharebar__btn sharebar__btn--primary" onClick={nativeShare}>
            {m.shareBar.native}
          </button>
        )}
        {intents.slice(0, 2).map((p) => (
          <IntentLink key={p} platform={p} href={shareIntentUrl(p, { landingUrl: landing(p), youtubeUrl: youtube, caption, rtl: captionRtl })} label={m.shareBar.platforms[p]} onClick={() => record(p)} />
        ))}
        <button
          type="button"
          className="sharebar__btn"
          onClick={() => {
            record("instagram_copy");
            void copyText(captionWithUrl(caption, landing("instagram_copy"), captionRtl), m.shareBar.captionCopied.replace("{app}", m.shareBar.platforms.instagram_copy));
          }}
        >
          {m.shareBar.platforms.instagram_copy}
        </button>
        <button
          type="button"
          className="sharebar__btn"
          onClick={() => {
            record("tiktok_copy");
            void copyText(captionWithUrl(caption, landing("tiktok_copy"), captionRtl), m.shareBar.captionCopied.replace("{app}", m.shareBar.platforms.tiktok_copy));
          }}
        >
          {m.shareBar.platforms.tiktok_copy}
        </button>
        {intents.slice(2).map((p) => (
          <IntentLink key={p} platform={p} href={shareIntentUrl(p, { landingUrl: landing(p), youtubeUrl: youtube, caption, rtl: captionRtl })} label={m.shareBar.platforms[p]} onClick={() => record(p)} />
        ))}
        <button
          type="button"
          className="sharebar__btn"
          onClick={() => {
            record("copy_link");
            void copyText(landing("copy_link"), m.shareBar.linkCopied);
          }}
        >
          {m.shareBar.copyLink}
        </button>
      </div>
      <p className="visually-hidden">{rtl ? "" : ""}</p>
    </section>
  );
}

function IntentLink({ platform, href, label, onClick }: { platform: IntentPlatform; href: string; label: string; onClick: () => void }) {
  return (
    <a className="sharebar__btn" href={href} target="_blank" rel="noopener noreferrer" data-platform={platform} onClick={onClick}>
      {label}
    </a>
  );
}
